import {
  BreakdownRow,
  InsuranceMetricDialog,
  InsuranceMonthlyChart,
  InsurancePlantDonut,
  InsuranceRegionBreakdown,
  InsuranceStatCards,
} from '@/features/insurance/components';
import {
  useInsuranceChart,
  useInsuranceContractByPlant,
  useInsuranceContractByRegion,
  useInsuranceFilters,
  useInsuranceStats,
  useInsuranceStatsBreakdown,
} from '@/features/insurance/hooks';
import { InsuranceMetric } from '@/features/insurance/model';
import { useDialog } from '@/hooks/useDialog';
import { useTranslation } from 'react-i18next';

export const Insurance = () => {
  const { t } = useTranslation();
  const { openDialog, closeDialog } = useDialog();
  const { year, regionCode, districtCode, plantId, params } =
    useInsuranceFilters();

  const { data: stats, isLoading: isStatsLoading } = useInsuranceStats(params);
  const { data: chart, isLoading: isChartLoading } = useInsuranceChart(params);

  const { data: byPlant, isLoading: isByPlantLoading } =
    useInsuranceContractByPlant({
      year,
      region_code: regionCode,
      district_code: districtCode,
    });

  /** Hudud tanlanmagan bo'lsa — viloyatlar kesimi. */
  const { data: byRegion, isLoading: isByRegionLoading } =
    useInsuranceContractByRegion({
      year,
      district_code: districtCode,
      plant_id: plantId,
    });

  /** Hudud tanlanganda `breakdown` o'sha viloyatning tumanlarini qaytaradi. */
  const { data: byDistrict, isLoading: isByDistrictLoading } =
    useInsuranceStatsBreakdown({
      metric: 'concluded_contract_count',
      year,
      region_code: regionCode,
      plant_id: plantId,
    });

  const breakdownRows: BreakdownRow[] = regionCode
    ? (byDistrict?.items || []).map((item) => ({
        id: item.district_code ?? item.region_code ?? item.value,
        name: item.district_name || item.region_name || '-',
        value: Number(item.value || 0),
      }))
    : (byRegion || []).map((row) => ({
        id: row.region_code,
        name: row.region_name,
        value: row.contract_count,
      }));

  /** Strelka faqat "Barcha hududlar" holatida ma'noli — viloyat tanlanganda
   *  kesim allaqachon bitta viloyatga tor bo'ladi. */
  const handleMetricOpen = (metric: InsuranceMetric, title: string) => {
    openDialog({
      view: (
        <InsuranceMetricDialog
          metric={metric}
          title={title}
          year={year}
          plantId={plantId}
          closeDialog={closeDialog}
        />
      ),
    });
  };

  return (
    <div className="flex flex-col gap-6 px-6 pb-6">
      <InsuranceStatCards
        data={stats}
        isLoading={isStatsLoading}
        onMetricOpen={regionCode ? undefined : handleMetricOpen}
      />

      <InsuranceMonthlyChart data={chart} isLoading={isChartLoading} />

      {/* items-stretch (default) — ikkala blok bir xil balandlikda turadi. */}
      <div className="grid grid-cols-2 gap-6">
        <InsuranceRegionBreakdown
          className="h-full"
          title={t(
            regionCode
              ? 'insurance_contracts_by_district'
              : 'insurance_contracts_by_region',
          )}
          data={breakdownRows}
          isLoading={regionCode ? isByDistrictLoading : isByRegionLoading}
        />

        <InsurancePlantDonut
          className="h-full"
          data={byPlant}
          isLoading={isByPlantLoading}
        />
      </div>
    </div>
  );
};
